import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { BookingEducationApi } from '@core/apis/booking-education/booking-education.api';
import { BookingEducationProgramDTO } from '@core/apis/booking-education/booking-education.dto';
import { BookingRehabilitationApi } from '@core/apis/booking-rehabilitation/booking-rehabilitation.api';
import { BookingServiceApi } from '@core/apis/booking-service/booking-service.api';
import { MoodleApi } from '@core/apis/moodle/moodle.api';
import { BookingStorageEnum } from '@core/enums/booking.enum';
import {
  EducationBookingStatusEnum,
  ServiceBookingStatusEnum,
  OrderByEnum,
  PaymentTypeEnum,
} from '@oranta-mis/sdk';
import { PriceHelper } from '@core/helpers/price.helper';
import { TranslocoHelper } from '@core/helpers/transloco.helper';
import { IPaginationGet } from '@core/interfaces/interface';
import { FormatTimePipe } from '@core/pipes/format-time.pipe';
import { ReusableOrderMethodsService } from '@core/services/reusable-order-methods.service';
import { UserStore } from '@core/stores/user.store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IButtonClickEvent, ITableChangesConfig, ITableRow, Table, TableColumn } from '@oranta-mis/ui-library';
import { OrderDetailModalComponent } from '../order-detail-modal/order-detail-modal.component';
import { educationOrderListColumnHeader } from './education';

interface IRenderTable extends ITableRow<BookingEducationProgramDTO> {
  education: string;
}

@Component({
  selector: 'app-education',
  imports: [Table, OrderDetailModalComponent],
  standalone: true,
  templateUrl: './education.component.html',
  styleUrl: './education.component.scss',
})
@UntilDestroy()
export class EducationOrderComponent extends ReusableOrderMethodsService implements OnInit {
  public columnHeader!: TableColumn[];
  public programHistoryPaginated!: IPaginationGet<BookingEducationProgramDTO[]>;
  public programHistory: IRenderTable[] = [];

  @ViewChild(OrderDetailModalComponent)
  orderDetailModal!: OrderDetailModalComponent;

  private config!: ITableChangesConfig;

  constructor(
    private readonly moodleApi: MoodleApi,
    protected override bookingEducationApi: BookingEducationApi,
    private readonly userStore: UserStore,
    protected override bookingServiceApi: BookingServiceApi,
    protected override bookingRehabilitationApi: BookingRehabilitationApi,
    private readonly i18n: TranslocoHelper,
  ) {
    super(bookingEducationApi, bookingRehabilitationApi, bookingServiceApi);
  }

  ngOnInit(): void {
    this.columnHeader = educationOrderListColumnHeader(this.i18n);
  }

  public setConfig(config: ITableChangesConfig): void {
    this.config = config;
    this.getList(config);
  }

  public renderObject(arr: BookingEducationProgramDTO[]): IRenderTable[] {
    return arr.map((v) => {
      const { uid, clinic, education, startDate, updatedAt, status } = v;
      const { name, direction, complexity, price, paymentType, format, formatTime } = education;
      const datePipe = new DatePipe('en-US');
      const priceFormatted = new PriceHelper().centsToPrice(price);
      const durationFormatted = new FormatTimePipe(this.i18n).transform({
        format,
        formatTime,
      });
      const startDateFormatted = datePipe.transform(startDate, 'dd-MM-yyyy');
      const updatedAtFormatted = datePipe.transform(updatedAt, 'HH:mm, dd-MM-yyyy');

      let item = {
        id: v.id,
        item: v,
        education: `<span class="badge bg-${v.status}">${this.i18n.tr(status)}</span>\n
		  <b><u>#${uid}</u></b>
		  <b>${clinic.longName}</b>
		  <b>${name}</b>\n
		  <u>${this.i18n.tr('direction')}</u>:&nbsp;${this.i18n.tr(direction)},
		  <u>${this.i18n.tr('complexity')}</u>:&nbsp;${this.i18n.tr(complexity)},
		  <u>${this.i18n.tr('price')}</u>:&nbsp;${priceFormatted}&nbsp;${this.i18n.tr('uah')},
 		  <u>${this.i18n.tr('payment_type')}</u>:&nbsp;${this.i18n.tr(paymentType)},
		  <u>${this.i18n.tr('duration')}</u>:&nbsp;${durationFormatted},
		  <u>${this.i18n.tr('start_date')}</u>:&nbsp;${startDateFormatted},
		  <u>${this.i18n.tr('updated_at')}</u>:&nbsp;${updatedAtFormatted},
		  <u>${this.i18n.tr('moodle_link')}</u>:&nbsp;`,
        buttonConfig: {
          pay: {
            action: 'pay',
            label: 'pay',
            icon: 'bi-credit-card',
            disabled: false,
            hidden:
              v.status == EducationBookingStatusEnum.accepted && v.education.paymentType == PaymentTypeEnum.online
                ? false
                : true,
          },
          cancel: { action: 'cancel', label: 'cancel', icon: 'bi-x-circle', disabled: false, hidden: false },
          details: { action: 'details', label: 'details', icon: 'bi-info-circle', disabled: false, hidden: false },
        },
      };

      if (v.education.moodleLink) {
        if (v.status == EducationBookingStatusEnum.paid || v.moodleAccess) {
          item.education += `<a href='${v.education.moodleLink}'>${v.education.moodleLink}</a>`;
          const url = new URL(v.education.moodleLink);
          const params = new URLSearchParams(url.search);
          const id = params.get('id');

          if (id)
            this.moodleApi
              .getCourseById(Number(id))
              .pipe(untilDestroyed(this))
              .subscribe((moodle) => {
                if (moodle) {
                  const start = datePipe.transform(new Date(moodle.startdate * 1000), 'HH:mm, dd/MM/yyyy');
                  const end = datePipe.transform(new Date(moodle.enddate * 1000), 'HH:mm, dd/MM/yyyy');
                  item.education += `\n\n<b>${this.i18n.tr('available')}</b>\n\n${start} - ${end}`;
                }
              });
        } else item.education += `<i>${this.i18n.tr('available_after_payment')}</i>`;
      } else item.education += `-`;
      return item;
    });
  }

  public onActionHandler(event: IButtonClickEvent<IRenderTable>): void {
    switch (event.action) {
      case 'pay':
        this.pay(event.row.item.id, BookingStorageEnum.education);
        break;
      case 'details':
        this.details(event.row.item);
        break;
      case 'cancel':
        this.cancel(event.row.item);
        break;
      default:
        break;
    }
  }

  private details(item: BookingEducationProgramDTO): void {
    this.orderDetailModal.order = item;
    this.orderDetailModal.orderType = BookingStorageEnum.education;
    this.orderDetailModal.program = item.education;
    this.orderDetailModal.openModal();
  }

  private cancel(item: BookingEducationProgramDTO): void {
    this.isLoading = true;
    this.bookingEducationApi
      .setStatus(item.id, {
        status: ServiceBookingStatusEnum.declined,
      })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => (this.getList(this.config), (this.isLoading = false)),
        error: () => (this.isLoading = false),
      });
  }

  private getList(config: ITableChangesConfig): void {
    this.isLoading = true;
    let settings = {
      pagination: {
        page: config?.page,
        perPage: config?.perPage,
      },
      where: {
        patientId: this.userStore.get()?.id as number,
      },
      orderBy: {
        createdAt: OrderByEnum.desc,
      },
    };
    this.bookingEducationApi
      .gets(settings)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (v) => {
          this.programHistory = this.renderObject(v.list);
          this.programHistoryPaginated = v;
          this.isLoading = false;
        },
        error: () => (this.isLoading = false),
      });
  }
}
